import { useState, useEffect } from 'react';
import { Mail, Settings, FolderOpen, BarChart3, Ban, Activity, CheckCircle, XCircle, RefreshCw, Play, Square, Eye, EyeOff, Server, Search, ChevronLeft, ChevronRight, X, FolderPlus, FlaskConical, Inbox, FolderX, VolumeX } from '../../icons/wp-icons';
import toast from 'react-hot-toast';
import { emailAgentApi } from '../../api/emailAgentApi';
import { emailOAuthApi } from '../../api/emailOAuthApi';
import { settingsApi } from '../../api/settingsApi';
import { kiZentraleApi } from '../../api/kiZentraleApi';
import { useEmbed } from '../../hooks/useEmbed';
import { censorName, censorEmail, censorSubject, censorBody } from '../../utils/demoCensor';

// HTML aus E-Mail-Inhalten entfernen
function stripHtmlTags(text) {
  if (!text) return '';
  if (!text.includes('<')) return text;
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return doc.body.textContent || '';
}

const SUB_TABS = [
  { id: 'status', label: 'Status', icon: Activity },
  { id: 'provider', label: 'Provider', icon: Mail },
  { id: 'sortierung', label: 'Sortierung', icon: FolderOpen },
  { id: 'logs', label: 'Logs', icon: BarChart3 },
  { id: 'blacklist', label: 'Blacklist', icon: Ban },
  { id: 'antwortsperre', label: 'Antwort-Sperre', icon: VolumeX },
];

// Provider Icons
const GmailIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
  </svg>
);

const MicrosoftIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
    <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
    <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
    <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
  </svg>
);

export default function EmailAgent({ readOnly }) {
  const { isEmbed } = useEmbed();
  const [activeTab, setActiveTab] = useState('status');
  const [agentStatus, setAgentStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Provider / IMAP/SMTP
  const [providerStatus, setProviderStatus] = useState({ gmail: { connected: false }, outlook: { connected: false } });
  const [connecting, setConnecting] = useState(null);
  const [emailSubTab, setEmailSubTab] = useState('gmail');
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState('993');
  const [imapUser, setImapUser] = useState('');
  const [imapPassword, setImapPassword] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [providerSaving, setProviderSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  // Processed Emails (Logs)
  const [processedEmails, setProcessedEmails] = useState([]);
  const [emailsPagination, setEmailsPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [emailsFilter, setEmailsFilter] = useState({ intent: 'ALL', search: '' });
  const [intents, setIntents] = useState([]);
  const [viewEmailData, setViewEmailData] = useState(null);
  const [viewEmailLoading, setViewEmailLoading] = useState(false);

  // Sorting
  const [sortingConfig, setSortingConfig] = useState(null);
  const [sortingFolders, setSortingFolders] = useState([]);
  const [sortingSaving, setSortingSaving] = useState(false);
  const [sortingInitializing, setSortingInitializing] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Blacklist
  const [blacklist, setBlacklist] = useState([]);
  const [newBlacklistEmail, setNewBlacklistEmail] = useState('');

  // Auto-Antwort-Sperre
  const [antwortSperren, setAntwortSperren] = useState([]);
  const [newSperreEmail, setNewSperreEmail] = useState('');

  // Kontaktformular
  const [kontaktformularEmails, setKontaktformularEmails] = useState([]);
  const [newKontaktformularEmail, setNewKontaktformularEmail] = useState('');
  const [kontaktformularSaving, setKontaktformularSaving] = useState(false);

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') loadProcessedEmails(1);
    if (activeTab === 'blacklist') loadBlacklist();
    if (activeTab === 'antwortsperre') loadAntwortSperren();
    if (activeTab === 'sortierung') loadSorting();
    if (activeTab === 'provider') { loadProviderStatus(); loadImapSmtpSettings(); }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'logs') loadProcessedEmails(1);
  }, [emailsFilter]);

  const loadInitial = async () => {
    try {
      const [statusData, statsData, intentsData, sperrenData] = await Promise.all([
        emailAgentApi.getStatus().catch(() => null),
        emailAgentApi.getStats?.().catch(() => null),
        emailAgentApi.getIntents?.().catch(() => []),
        emailAgentApi.getAutoAntwortSperren().catch(() => []),
      ]);
      setAgentStatus(statusData);
      setStats(statsData);
      setIntents(intentsData || []);
      setAntwortSperren(Array.isArray(sperrenData) ? sperrenData : []);
      // Provider-Status für Badges laden
      loadProviderStatus();
      loadImapSmtpSettings();
    } catch (err) {
      console.error('Email-Agent Status laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProviderStatus = async () => {
    try {
      const status = await emailOAuthApi.getAllStatus('agent');
      setProviderStatus({
        gmail: status.gmail || { connected: false },
        outlook: status.outlook || { connected: false }
      });
    } catch (err) {
      console.error('Provider status error:', err);
    }
  };

  const loadImapSmtpSettings = async () => {
    try {
      const settings = await settingsApi.getAll();
      if (settings.email_agent_imap_host) setImapHost(settings.email_agent_imap_host);
      if (settings.email_agent_imap_port) setImapPort(settings.email_agent_imap_port);
      if (settings.email_agent_imap_user) setImapUser(settings.email_agent_imap_user);
      if (settings.email_agent_imap_password) setImapPassword(settings.email_agent_imap_password);
      if (settings.email_agent_smtp_host) setSmtpHost(settings.email_agent_smtp_host);
      if (settings.email_agent_smtp_port) setSmtpPort(settings.email_agent_smtp_port);
      if (settings.email_agent_smtp_user) setSmtpUser(settings.email_agent_smtp_user);
      if (settings.email_agent_smtp_password) setSmtpPassword(settings.email_agent_smtp_password);
    } catch (err) {
      console.error('IMAP/SMTP settings load error:', err);
    }
  };

  const loadProcessedEmails = async (page = 1) => {
    try {
      const params = {
        page,
        limit: 20,
        ...(emailsFilter.intent !== 'ALL' && { intent: emailsFilter.intent }),
        ...(emailsFilter.search && { search: emailsFilter.search })
      };
      const data = await emailAgentApi.getProcessedEmails(params);
      setProcessedEmails(data.emails || []);
      setEmailsPagination(data.pagination || { page, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Processed emails laden fehlgeschlagen:', err);
    }
  };

  const loadBlacklist = async () => {
    try {
      const data = await emailAgentApi.getBlacklist();
      setBlacklist(data.blacklist || data || []);
    } catch (err) {
      console.error('Blacklist laden fehlgeschlagen:', err);
    }
  };

  const loadAntwortSperren = async () => {
    try {
      const data = await emailAgentApi.getAutoAntwortSperren();
      setAntwortSperren(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Antwort-Sperren laden fehlgeschlagen:', err);
    }
  };

  const handleAddAntwortSperre = async () => {
    if (!newSperreEmail.trim()) return;
    try {
      await emailAgentApi.addAutoAntwortSperre(newSperreEmail.trim());
      setNewSperreEmail('');
      toast.success('Auto-Antwort-Sperre aktiviert');
      loadAntwortSperren();
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleRemoveAntwortSperre = async (email) => {
    try {
      await emailAgentApi.removeAutoAntwortSperre(email);
      toast.success('Auto-Antwort-Sperre entfernt');
      loadAntwortSperren();
    } catch (err) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleToggleAntwortSperre = async (email) => {
    const existing = antwortSperren.find(s => s.kontakt === email.toLowerCase());
    if (existing) {
      await handleRemoveAntwortSperre(email);
    } else {
      try {
        await emailAgentApi.addAutoAntwortSperre(email);
        toast.success('Auto-Antwort-Sperre aktiviert');
        loadAntwortSperren();
      } catch (err) {
        toast.error('Fehler');
      }
    }
  };

  const loadSorting = async () => {
    try {
      const [statusData, foldersData, kiSettings] = await Promise.all([
        emailAgentApi.getSortingStatus?.().catch(() => null),
        emailAgentApi.getSortingFolders?.().catch(() => []),
        kiZentraleApi.getSettings().catch(() => null),
      ]);
      setSortingConfig(statusData);
      setSortingFolders(foldersData || []);
      if (kiSettings?.kontaktformularEmails) {
        setKontaktformularEmails(kiSettings.kontaktformularEmails);
      }
    } catch (err) {
      console.error('Sorting Config laden fehlgeschlagen:', err);
    }
  };

  const handleSortingConfigSave = async (updates) => {
    setSortingSaving(true);
    try {
      const result = await emailAgentApi.updateSortingConfig(updates);
      setSortingConfig(result.config || { ...sortingConfig, ...updates });
      toast.success('Sortierung aktualisiert');
    } catch (err) {
      toast.error('Fehler beim Speichern: ' + (err.response?.data?.error || err.message));
    } finally {
      setSortingSaving(false);
    }
  };

  const handleInitializeSorting = async () => {
    setSortingInitializing(true);
    try {
      const result = await emailAgentApi.initializeSorting();
      if (result.success) {
        toast.success('Ordnerstruktur erfolgreich erstellt');
        loadSorting();
      } else {
        toast.error(result.error || 'Initialisierung fehlgeschlagen');
      }
    } catch (err) {
      toast.error('Fehler: ' + (err.response?.data?.error || err.message));
    } finally {
      setSortingInitializing(false);
    }
  };

  const handleTestSorting = async () => {
    if (!testEmail.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const result = await emailAgentApi.testSortingMatch({
        fromAddress: testEmail.trim(),
        fromName: testName.trim() || undefined,
      });
      setTestResult(result);
    } catch (err) {
      toast.error('Test fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    } finally {
      setTestLoading(false);
    }
  };

  const handleStartStop = async (action) => {
    try {
      if (action === 'start') {
        await emailAgentApi.start();
        toast.success('E-Mail-Agent gestartet');
      } else {
        await emailAgentApi.stop();
        toast.success('E-Mail-Agent gestoppt');
      }
      setTimeout(loadInitial, 2000);
    } catch (err) {
      toast.error('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleOAuthConnect = async (provider) => {
    try {
      setConnecting(provider);
      let authUrl;
      if (provider === 'gmail') {
        authUrl = (await emailOAuthApi.gmail.getAuthUrl('agent')).authUrl;
      } else if (provider === 'outlook') {
        authUrl = (await emailOAuthApi.outlook.getAuthUrl('agent')).authUrl;
      }

      const width = 550, height = 650;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(authUrl, `${provider} OAuth`, `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`);

      const checkPopup = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopup);
          setConnecting(null);
          setTimeout(async () => {
            await loadProviderStatus();
            try {
              await emailAgentApi.switchProvider(provider);
              setImapHost(''); setImapPort('993'); setImapUser(''); setImapPassword('');
              setSmtpHost(''); setSmtpPort('587'); setSmtpUser(''); setSmtpPassword('');
              setTestResults({});
            } catch (switchErr) {
              console.error('Provider switch error:', switchErr);
            }
            loadInitial();
          }, 500);
        }
      }, 500);
    } catch (err) {
      toast.error(`Fehler beim Verbinden: ${err.message}`);
      setConnecting(null);
    }
  };

  const handleOAuthDisconnect = async (provider) => {
    if (!confirm(`${provider === 'gmail' ? 'Gmail' : 'Outlook'} Verbindung wirklich trennen?`)) return;
    try {
      if (provider === 'gmail') await emailOAuthApi.gmail.disconnect('agent');
      else if (provider === 'outlook') await emailOAuthApi.outlook.disconnect('agent');
      await loadProviderStatus();
      loadInitial();
    } catch (err) {
      toast.error('Fehler beim Trennen');
    }
  };

  const handleSaveImapSmtp = async () => {
    setProviderSaving(true);
    try {
      await settingsApi.updateBulk({
        email_agent_imap_host: imapHost?.trim() || '',
        email_agent_imap_port: imapPort,
        email_agent_imap_user: imapUser?.trim() || '',
        email_agent_imap_password: imapPassword,
        email_agent_smtp_host: smtpHost?.trim() || '',
        email_agent_smtp_port: smtpPort,
        email_agent_smtp_user: smtpUser?.trim() || '',
        email_agent_smtp_password: smtpPassword
      });
      toast.success('IMAP/SMTP-Einstellungen gespeichert');

      // Auto-test
      const hasImap = imapHost?.trim() && imapUser?.trim() && imapPassword;
      const hasSmtp = smtpHost?.trim() && smtpUser?.trim() && smtpPassword;

      if (hasImap) {
        setTesting(p => ({ ...p, imap: true }));
        try {
          await emailAgentApi.testImap();
          setTestResults(p => ({ ...p, imap: { success: true, message: 'IMAP OK' } }));
        } catch (err) {
          setTestResults(p => ({ ...p, imap: { success: false, message: err.response?.data?.error || err.message } }));
        }
        setTesting(p => ({ ...p, imap: false }));
      }

      if (hasSmtp) {
        setTesting(p => ({ ...p, smtp: true }));
        try {
          await emailAgentApi.testSmtp();
          setTestResults(p => ({ ...p, smtp: { success: true, message: 'SMTP OK' } }));
        } catch (err) {
          setTestResults(p => ({ ...p, smtp: { success: false, message: err.response?.data?.error || err.message } }));
        }
        setTesting(p => ({ ...p, smtp: false }));
      }

      if (hasImap && hasSmtp) {
        try {
          await emailAgentApi.switchProvider('imap');
          await loadProviderStatus();
          loadInitial();
        } catch (e) {}
      }
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setProviderSaving(false);
    }
  };

  const handleAddBlacklist = async () => {
    if (!newBlacklistEmail) return;
    try {
      await emailAgentApi.addToBlacklist(newBlacklistEmail, 'Manuell hinzugefügt');
      setNewBlacklistEmail('');
      loadBlacklist();
      toast.success('Zur Blacklist hinzugefügt');
    } catch (err) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const handleViewEmail = async (email) => {
    setViewEmailLoading(true);
    try {
      const fullEmail = await emailAgentApi.getProcessedEmail(email.id);
      setViewEmailData(fullEmail);
    } catch (err) {
      toast.error('Fehler beim Laden der E-Mail');
    } finally {
      setViewEmailLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isRunning = agentStatus?.running || agentStatus?.active || agentStatus?.status === 'running';

  return (
    <div className="space-y-4">
      {/* Sub-Tabs */}
      <div className="flex overflow-x-auto gap-1 bg-gray-100 rounded-lg p-1">
        {SUB_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.id ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">E-Mail-Agent Status</h3>
            <div className="flex items-center gap-2">
              {isRunning ? (
                <span className="flex items-center gap-1.5 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" /> Läuft
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                  <XCircle className="w-4 h-4" /> Gestoppt
                </span>
              )}
            </div>
          </div>

          <p className="text-sm text-text-muted">
            Autonomiestufe, Pflichtfelder und Preisauskunft werden zentral unter dem Tab "Einstellungen" konfiguriert.
          </p>

          {/* Aktiver Provider — im Demo-Embed wird die konkrete Adresse ausgeblendet */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <span className="text-sm text-text-muted">Provider:</span>
            {providerStatus.gmail?.connected ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Gmail{isEmbed ? '' : ` – ${providerStatus.gmail.email}`}
              </span>
            ) : providerStatus.outlook?.connected ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                Outlook{isEmbed ? '' : ` – ${providerStatus.outlook.email}`}
              </span>
            ) : imapUser ? (
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                IMAP/SMTP{isEmbed ? '' : ` – ${imapUser}`}
              </span>
            ) : (
              <span className="text-sm text-amber-600 font-medium">Kein Provider konfiguriert</span>
            )}
          </div>

          {!readOnly && !isEmbed && (
            <div className="flex gap-2">
              {isRunning ? (
                <button onClick={() => handleStartStop('stop')} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">
                  <Square className="w-4 h-4" /> Stoppen
                </button>
              ) : (
                <button onClick={() => handleStartStop('start')} className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600">
                  <Play className="w-4 h-4" /> Starten
                </button>
              )}
              <button onClick={() => { handleStartStop('stop'); setTimeout(() => handleStartStop('start'), 2000); }} className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-gray-50">
                <RefreshCw className="w-4 h-4" /> Neustart
              </button>
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-border">
              <StatCard label="Verarbeitet" value={stats.today?.emailsVerarbeitet || 0} />
              <StatCard label="Beantwortet" value={stats.today?.antwortenGesendet || 0} />
              <StatCard label="Neue Kunden" value={stats.today?.neueKunden || 0} />
              <StatCard label="Termine" value={stats.today?.termineAngefragt || 0} />
            </div>
          )}
        </div>
      )}

      {/* Provider Tab - Full IMAP/SMTP/OAuth Config */}
      {activeTab === 'provider' && (
        <div className="space-y-4">
          {/* OAuth Providers */}
          <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
            <h3 className="text-lg font-semibold">E-Mail-Provider verbinden</h3>
            <p className="text-sm text-text-muted">
              Verbinden Sie Gmail oder Outlook per OAuth, oder konfigurieren Sie IMAP/SMTP manuell.
            </p>

            {/* Provider Sub-Tabs */}
            <div className="flex gap-1 bg-gray-50 rounded-lg p-1">
              {[
                { id: 'gmail', label: 'Gmail', Icon: GmailIcon },
                { id: 'outlook', label: 'Outlook', Icon: MicrosoftIcon },
                { id: 'smtp', label: 'IMAP/SMTP', Icon: Server },
              ].map(({ id, label, Icon }) => {
                const isActiveProvider = id === 'gmail' ? providerStatus.gmail?.connected
                  : id === 'outlook' ? providerStatus.outlook?.connected
                  : !providerStatus.gmail?.connected && !providerStatus.outlook?.connected && !!imapUser;
                return (
                  <button
                    key={id}
                    onClick={() => setEmailSubTab(id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium flex-1 justify-center transition ${
                      emailSubTab === id ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {isActiveProvider && (
                      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" title="Aktiver Provider" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Gmail */}
            {emailSubTab === 'gmail' && (
              <div className="p-4 rounded-lg border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GmailIcon className="w-6 h-6" />
                    <div>
                      <p className="text-sm font-medium">Gmail</p>
                      <p className="text-xs text-text-muted">
                        {providerStatus.gmail?.connected
                          ? `Verbunden: ${providerStatus.gmail.email || 'OK'}`
                          : 'Nicht verbunden'}
                      </p>
                    </div>
                  </div>
                  {providerStatus.gmail?.connected ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      {!readOnly && !isEmbed && (
                        <button onClick={() => handleOAuthDisconnect('gmail')} className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                          Trennen
                        </button>
                      )}
                    </div>
                  ) : (
                    !readOnly && !isEmbed && (
                      <button
                        onClick={() => handleOAuthConnect('gmail')}
                        disabled={connecting === 'gmail'}
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
                      >
                        {connecting === 'gmail' ? 'Verbinde...' : 'Verbinden'}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Outlook */}
            {emailSubTab === 'outlook' && (
              <div className="p-4 rounded-lg border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MicrosoftIcon className="w-6 h-6" />
                    <div>
                      <p className="text-sm font-medium">Outlook / Microsoft 365</p>
                      <p className="text-xs text-text-muted">
                        {providerStatus.outlook?.connected
                          ? `Verbunden: ${providerStatus.outlook.email || 'OK'}`
                          : 'Nicht verbunden'}
                      </p>
                    </div>
                  </div>
                  {providerStatus.outlook?.connected ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      {!readOnly && !isEmbed && (
                        <button onClick={() => handleOAuthDisconnect('outlook')} className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
                          Trennen
                        </button>
                      )}
                    </div>
                  ) : (
                    !readOnly && !isEmbed && (
                      <button
                        onClick={() => handleOAuthConnect('outlook')}
                        disabled={connecting === 'outlook'}
                        className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
                      >
                        {connecting === 'outlook' ? 'Verbinde...' : 'Verbinden'}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            {/* IMAP/SMTP Manual Config */}
            {emailSubTab === 'smtp' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-border bg-blue-50">
                  <p className="text-sm text-blue-700">
                    Manuelle IMAP/SMTP-Konfiguration für Provider, die kein OAuth unterstützen.
                    Bei Gmail/Outlook wird OAuth empfohlen.
                  </p>
                </div>

                {/* IMAP */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    IMAP (Posteingang)
                    {testResults.imap && (
                      <span className={`text-xs px-2 py-0.5 rounded ${testResults.imap.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {testResults.imap.message}
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Server</label>
                      <input type="text" value={imapHost} onChange={(e) => setImapHost(e.target.value)} disabled={readOnly} placeholder="imap.provider.de" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Port</label>
                      <input type="text" value={imapPort} onChange={(e) => setImapPort(e.target.value)} disabled={readOnly} placeholder="993" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Benutzername</label>
                      <input type="text" value={imapUser} onChange={(e) => setImapUser(e.target.value)} disabled={readOnly} placeholder="email@provider.de" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Passwort</label>
                      <div className="relative">
                        <input type={showPasswords ? 'text' : 'password'} value={imapPassword} onChange={(e) => setImapPassword(e.target.value)} disabled={readOnly} placeholder="••••••••" className="w-full px-3 py-2 border border-border rounded-lg text-sm pr-10" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text">
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SMTP */}
                <div className="space-y-3 pt-3 border-t border-border">
                  <h4 className="text-sm font-semibold flex items-center gap-2">
                    SMTP (Postausgang)
                    {testResults.smtp && (
                      <span className={`text-xs px-2 py-0.5 rounded ${testResults.smtp.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {testResults.smtp.message}
                      </span>
                    )}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Server</label>
                      <input type="text" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} disabled={readOnly} placeholder="smtp.provider.de" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Port</label>
                      <input type="text" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} disabled={readOnly} placeholder="587" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Benutzername</label>
                      <input type="text" value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} disabled={readOnly} placeholder="email@provider.de" className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-text-muted block mb-1">Passwort</label>
                      <div className="relative">
                        <input type={showPasswords ? 'text' : 'password'} value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} disabled={readOnly} placeholder="••••••••" className="w-full px-3 py-2 border border-border rounded-lg text-sm pr-10" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text">
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {!readOnly && !isEmbed && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSaveImapSmtp}
                      disabled={providerSaving}
                      className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50"
                    >
                      {providerSaving ? 'Speichere...' : 'Speichern & Testen'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sortierung Tab */}
      {activeTab === 'sortierung' && (
        <div className="space-y-4">
          {/* Haupteinstellungen */}
          <div className="bg-surface rounded-card shadow-card p-5 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">E-Mail-Sortierung</h3>
                <p className="text-sm text-text-muted mt-0.5">
                  Eingehende E-Mails automatisch in Kundenordner sortieren
                </p>
              </div>
              <button
                onClick={() => loadSorting()}
                className="p-2 text-text-muted hover:text-text rounded-lg hover:bg-gray-100"
                title="Aktualisieren"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {sortingConfig ? (
              <div className="space-y-5">
                {/* Aktivierung Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    {sortingConfig.enabled ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        Automatische Sortierung {sortingConfig.enabled ? 'aktiv' : 'inaktiv'}
                      </p>
                      <p className="text-xs text-text-muted">
                        {sortingConfig.enabled
                          ? 'E-Mails werden automatisch in Kundenordner verschoben'
                          : 'E-Mails bleiben im Posteingang'}
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={!readOnly && !sortingSaving ? () => handleSortingConfigSave({ enabled: !sortingConfig.enabled }) : undefined}
                    className={`relative w-11 h-6 rounded-full transition flex-shrink-0 ${!readOnly ? 'cursor-pointer' : ''} ${sortingConfig.enabled ? 'bg-primary' : 'bg-gray-300'}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${sortingConfig.enabled ? 'translate-x-5' : ''}`} />
                  </div>
                </div>

                {/* Provider & Statistiken */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg border border-border text-center">
                    <p className="text-lg font-bold text-text">{sortingConfig.provider || '–'}</p>
                    <p className="text-xs text-text-muted">Provider</p>
                  </div>
                  <div className="p-3 rounded-lg border border-border text-center">
                    <p className="text-lg font-bold text-text">{sortingConfig.folderCount || 0}</p>
                    <p className="text-xs text-text-muted">Ordner</p>
                  </div>
                  <div className="p-3 rounded-lg border border-border text-center">
                    <p className="text-lg font-bold text-text">{sortingConfig.kundenCount || 0}</p>
                    <p className="text-xs text-text-muted">Kunden</p>
                  </div>
                  <div className="p-3 rounded-lg border border-border text-center">
                    <p className="text-lg font-bold text-text">{Math.round((sortingConfig.minConfidence || 0.7) * 100)}%</p>
                    <p className="text-xs text-text-muted">Min. Konfidenz</p>
                  </div>
                </div>

                {/* Konfiguration */}
                {sortingConfig.enabled && (
                  <div className="space-y-4 p-4 rounded-lg border border-border bg-gray-50">
                    <h4 className="text-sm font-semibold">Konfiguration</h4>

                    {/* Unbekannte E-Mails */}
                    <div>
                      <label className="text-xs text-text-muted block mb-1.5">
                        Aktion bei unbekanntem Absender
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {[
                          { value: 'folder', label: 'In "Unbekannt"-Ordner', desc: 'E-Mail wird in einen separaten Ordner verschoben', Icon: FolderOpen },
                          { value: 'inbox', label: 'Im Posteingang lassen', desc: 'E-Mail bleibt unverändert im Posteingang', Icon: Inbox },
                          { value: 'skip', label: 'Überspringen', desc: 'E-Mail wird ignoriert und nicht verarbeitet', Icon: FolderX },
                        ].map(({ value, label, desc, Icon }) => (
                          <button
                            key={value}
                            onClick={() => !readOnly && handleSortingConfigSave({ unknownAction: value })}
                            disabled={readOnly || sortingSaving}
                            className={`p-3 rounded-lg border text-left transition ${
                              sortingConfig.unknownAction === value
                                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                                : 'border-border bg-white hover:border-gray-300'
                            } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className={`w-4 h-4 ${sortingConfig.unknownAction === value ? 'text-primary' : 'text-gray-400'}`} />
                              <span className="text-sm font-medium">{label}</span>
                            </div>
                            <p className="text-xs text-text-muted">{desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Mindest-Konfidenz */}
                    <div>
                      <label className="text-xs text-text-muted block mb-1.5">
                        Mindest-Konfidenz für Kundenzuordnung
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={Math.round((sortingConfig.minConfidence || 0.7) * 100)}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) / 100;
                            setSortingConfig(prev => ({ ...prev, minConfidence: val }));
                          }}
                          onMouseUp={(e) => {
                            const val = parseInt(e.target.value) / 100;
                            if (!readOnly) handleSortingConfigSave({ minConfidence: val });
                          }}
                          onTouchEnd={(e) => {
                            const val = parseInt(e.target.value) / 100;
                            if (!readOnly) handleSortingConfigSave({ minConfidence: val });
                          }}
                          disabled={readOnly}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                        />
                        <span className="text-sm font-semibold w-12 text-right">
                          {Math.round((sortingConfig.minConfidence || 0.7) * 100)}%
                        </span>
                      </div>
                      <p className="text-xs text-text-muted mt-1">
                        {sortingConfig.minConfidence <= 0.5
                          ? 'Niedrig – mehr E-Mails werden zugeordnet, aber mit höherem Fehlerrisiko'
                          : sortingConfig.minConfidence >= 0.9
                            ? 'Hoch – nur sehr sichere Zuordnungen, viele E-Mails bleiben unsortiert'
                            : 'Ausgewogen – guter Kompromiss zwischen Zuordnung und Genauigkeit'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Ordner initialisieren */}
                {sortingConfig.enabled && !sortingConfig.provider && !readOnly && (
                  <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="flex items-start gap-3">
                      <FolderPlus className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-800">Ordnerstruktur nicht initialisiert</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Erstellen Sie die Basis-Ordnerstruktur, damit E-Mails sortiert werden können.
                        </p>
                        <button
                          onClick={handleInitializeSorting}
                          disabled={sortingInitializing}
                          className="mt-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 disabled:opacity-50"
                        >
                          {sortingInitializing ? 'Erstelle...' : 'Ordnerstruktur erstellen'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ordner initialisieren Button (wenn Provider vorhanden) */}
                {sortingConfig.enabled && sortingConfig.provider && !readOnly && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleInitializeSorting}
                      disabled={sortingInitializing}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      <FolderPlus className="w-4 h-4" />
                      {sortingInitializing ? 'Erstelle...' : 'Ordner neu initialisieren'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            )}
          </div>

          {/* Kundenordner-Liste */}
          {sortingConfig?.enabled && (
            <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">Kundenordner</h3>
                <span className="text-xs text-text-muted bg-gray-100 px-2 py-1 rounded-full">
                  {sortingFolders.length} Ordner
                </span>
              </div>

              {sortingFolders.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {sortingFolders.map((folder, i) => (
                    <div
                      key={folder.name || i}
                      className="flex items-center gap-2 p-2.5 rounded-lg border border-border hover:bg-gray-50"
                    >
                      <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <span className="text-sm truncate">{folder.name || folder}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-text-muted text-center py-4">
                  Noch keine Kundenordner vorhanden. Ordner werden automatisch beim Sortieren erstellt.
                </p>
              )}
            </div>
          )}

          {/* Test-Sortierung */}
          {sortingConfig?.enabled && (
            <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-text-muted" />
                Sortierung testen
              </h3>
              <p className="text-sm text-text-muted">
                Testen Sie, welchem Kunden eine E-Mail-Adresse zugeordnet wird – ohne tatsächlich zu verschieben.
              </p>

              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  <label className="text-xs text-text-muted block mb-1">E-Mail-Adresse *</label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="kunde@beispiel.de"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleTestSorting()}
                  />
                </div>
                <div className="sm:w-48">
                  <label className="text-xs text-text-muted block mb-1">Name (optional)</label>
                  <input
                    type="text"
                    value={testName}
                    onChange={(e) => setTestName(e.target.value)}
                    placeholder="Max Mustermann"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleTestSorting()}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={handleTestSorting}
                    disabled={testLoading || !testEmail.trim()}
                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                  >
                    {testLoading ? 'Teste...' : 'Testen'}
                  </button>
                </div>
              </div>

              {/* Test-Ergebnis */}
              {testResult && (
                <div className={`p-4 rounded-lg border ${
                  testResult.matchResult?.matched
                    ? 'border-green-200 bg-green-50'
                    : 'border-amber-200 bg-amber-50'
                }`}>
                  <div className="flex items-start gap-3">
                    {testResult.matchResult?.matched ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 space-y-2">
                      <p className={`text-sm font-medium ${testResult.matchResult?.matched ? 'text-green-800' : 'text-amber-800'}`}>
                        {testResult.matchResult?.matched
                          ? `Kunde erkannt: ${censorName(testResult.matchResult.kunde?.name, isEmbed)}`
                          : 'Kein Kunde zugeordnet'}
                      </p>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div>
                          <span className="text-text-muted">Zielordner:</span>{' '}
                          <span className="font-medium">{testResult.targetFolder}</span>
                        </div>
                        {testResult.matchResult?.matchType && (
                          <div>
                            <span className="text-text-muted">Match-Typ:</span>{' '}
                            <span className="font-medium">{testResult.matchResult.matchType}</span>
                          </div>
                        )}
                        {typeof testResult.matchResult?.confidence !== 'undefined' && (
                          <div>
                            <span className="text-text-muted">Konfidenz:</span>{' '}
                            <span className="font-medium">{Math.round(testResult.matchResult.confidence * 100)}%</span>
                          </div>
                        )}
                        {testResult.matchResult?.kunde?.email && (
                          <div>
                            <span className="text-text-muted">Kunden-E-Mail:</span>{' '}
                            <span className="font-medium">{censorEmail(testResult.matchResult.kunde.email, isEmbed)}</span>
                          </div>
                        )}
                      </div>

                      {testResult.matchResult?.candidates && testResult.matchResult.candidates.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-200">
                          <p className="text-xs text-text-muted mb-1">Mögliche Kandidaten:</p>
                          <div className="space-y-1">
                            {testResult.matchResult.candidates.map((c, i) => (
                              <p key={i} className="text-xs">
                                {c.name ? censorName(c.name, isEmbed) : censorEmail(c.email, isEmbed)} {c.confidence ? `(${Math.round(c.confidence * 100)}%)` : ''}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kontaktformular-Adressen */}
          <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
            <div>
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Mail className="w-4 h-4 text-text-muted" />
                Kontaktformular-Adressen
              </h3>
              <p className="text-sm text-text-muted mt-1">
                E-Mail-Adressen, über die Ihr Website-Kontaktformular versendet (z.B. info@meinefirma.de).
                Bei E-Mails von diesen Adressen werden die Kundendaten (Name, E-Mail, Telefon) aus dem Nachrichtentext extrahiert statt vom Absender.
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Hinweis: Diese Adressen werden auch dann verarbeitet, wenn sie gleichzeitig eine Mitarbeiter-E-Mail-Adresse sind.
              </p>
            </div>

            {/* Bestehende Adressen */}
            {kontaktformularEmails.length > 0 && (
              <div className="space-y-2">
                {kontaktformularEmails.map((email, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm font-medium">{email}</span>
                    </div>
                    {!readOnly && (
                      <button
                        onClick={async () => {
                          const updated = kontaktformularEmails.filter((_, idx) => idx !== i);
                          setKontaktformularSaving(true);
                          try {
                            await kiZentraleApi.updateSettings({ kontaktformularEmails: updated });
                            setKontaktformularEmails(updated);
                            toast.success('Kontaktformular-Adresse entfernt');
                          } catch (err) {
                            toast.error('Fehler beim Entfernen');
                          } finally {
                            setKontaktformularSaving(false);
                          }
                        }}
                        disabled={kontaktformularSaving}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Entfernen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Neue Adresse hinzufügen */}
            {!readOnly && (
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newKontaktformularEmail}
                  onChange={(e) => setNewKontaktformularEmail(e.target.value)}
                  placeholder="info@meinefirma.de"
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = newKontaktformularEmail.trim().toLowerCase();
                      if (!val || !val.includes('@')) return;
                      if (kontaktformularEmails.includes(val)) {
                        toast.error('Diese Adresse ist bereits eingetragen');
                        return;
                      }
                      const updated = [...kontaktformularEmails, val];
                      setKontaktformularSaving(true);
                      kiZentraleApi.updateSettings({ kontaktformularEmails: updated })
                        .then(() => {
                          setKontaktformularEmails(updated);
                          setNewKontaktformularEmail('');
                          toast.success('Kontaktformular-Adresse hinzugefügt');
                        })
                        .catch(() => toast.error('Fehler beim Speichern'))
                        .finally(() => setKontaktformularSaving(false));
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const val = newKontaktformularEmail.trim().toLowerCase();
                    if (!val || !val.includes('@')) return;
                    if (kontaktformularEmails.includes(val)) {
                      toast.error('Diese Adresse ist bereits eingetragen');
                      return;
                    }
                    const updated = [...kontaktformularEmails, val];
                    setKontaktformularSaving(true);
                    kiZentraleApi.updateSettings({ kontaktformularEmails: updated })
                      .then(() => {
                        setKontaktformularEmails(updated);
                        setNewKontaktformularEmail('');
                        toast.success('Kontaktformular-Adresse hinzugefügt');
                      })
                      .catch(() => toast.error('Fehler beim Speichern'))
                      .finally(() => setKontaktformularSaving(false));
                  }}
                  disabled={kontaktformularSaving || !newKontaktformularEmail.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
                >
                  Hinzufügen
                </button>
              </div>
            )}

            {kontaktformularEmails.length === 0 && (
              <p className="text-xs text-text-muted">
                Keine Kontaktformular-Adressen konfiguriert. E-Mails werden normal über die Absenderadresse zugeordnet.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Logs Tab - Verarbeitete E-Mails */}
      {activeTab === 'logs' && (
        <div className="bg-surface rounded-card shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Verarbeitete E-Mails</h3>
            <button onClick={() => loadProcessedEmails(emailsPagination.page)} className="p-2 text-text-muted hover:text-text">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Filter */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Suche nach Betreff, Absender..."
                value={emailsFilter.search}
                onChange={(e) => setEmailsFilter(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <select
              value={emailsFilter.intent}
              onChange={(e) => setEmailsFilter(prev => ({ ...prev, intent: e.target.value }))}
              className="px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="ALL">Alle Intents</option>
              {intents.map(i => (
                <option key={i.value} value={i.value}>{i.label || i.value}</option>
              ))}
            </select>
          </div>

          {processedEmails.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">Keine verarbeiteten E-Mails vorhanden</p>
          ) : (
            <div className="space-y-2">
              {processedEmails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => handleViewEmail(email)}
                  className="p-3 rounded-lg border border-border hover:bg-gray-50 cursor-pointer transition"
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{censorSubject(email.betreff || email.subject, isEmbed) || '(Kein Betreff)'}</p>
                      <p className="text-xs text-text-muted truncate">{censorEmail(email.absender || email.from, isEmbed)}</p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      {email.intent && (
                        <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                          {intents.find(i => i.value === email.intent)?.label || email.intent}
                        </span>
                      )}
                      <p className="text-xs text-text-muted mt-1">
                        {new Date(email.erstelltAm || email.createdAt).toLocaleString('de-DE')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {emailsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-3">
                  <p className="text-xs text-text-muted">{emailsPagination.total} E-Mails</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => loadProcessedEmails(emailsPagination.page - 1)}
                      disabled={emailsPagination.page <= 1}
                      className="p-1.5 border border-border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-text-muted">
                      {emailsPagination.page} / {emailsPagination.totalPages}
                    </span>
                    <button
                      onClick={() => loadProcessedEmails(emailsPagination.page + 1)}
                      disabled={emailsPagination.page >= emailsPagination.totalPages}
                      className="p-1.5 border border-border rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Email Detail Modal */}
      {viewEmailData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewEmailData(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-white rounded-t-xl">
              <h3 className="text-lg font-semibold truncate">{censorSubject(viewEmailData.betreff, isEmbed) || '(Kein Betreff)'}</h3>
              <button onClick={() => setViewEmailData(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-text-muted">Von:</span> <span className="font-medium">{censorEmail(viewEmailData.absender, isEmbed)}</span></div>
                <div><span className="text-text-muted">Datum:</span> {new Date(viewEmailData.erstelltAm || viewEmailData.createdAt).toLocaleString('de-DE')}</div>
                {viewEmailData.intent && (
                  <div><span className="text-text-muted">Intent:</span> <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{intents.find(i => i.value === viewEmailData.intent)?.label || viewEmailData.intent}</span></div>
                )}
                {viewEmailData.kunde && (
                  <div><span className="text-text-muted">Kunde:</span> <span className="font-medium">{viewEmailData.kunde.name ? censorName(viewEmailData.kunde.name, isEmbed) : censorEmail(viewEmailData.kunde.email, isEmbed)}</span></div>
                )}
              </div>

              {/* Auto-Antwort-Sperre Toggle */}
              {viewEmailData.absender && !readOnly && (
                <button
                  onClick={() => handleToggleAntwortSperre(viewEmailData.absender)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    antwortSperren.some(s => s.kontakt === viewEmailData.absender?.toLowerCase())
                      ? 'bg-primary/10 text-primary hover:bg-primary/20'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <VolumeX className="w-3.5 h-3.5" />
                  {antwortSperren.some(s => s.kontakt === viewEmailData.absender?.toLowerCase())
                    ? 'Antwort-Sperre aktiv – klicken zum Aufheben'
                    : 'Auto-Antwort für diesen Absender sperren'
                  }
                </button>
              )}

              {/* Original Content */}
              <div>
                <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Originalinhalt</p>
                <div className="p-3 rounded-lg bg-gray-50 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {censorBody(stripHtmlTags(viewEmailData.inhaltBereinigt || viewEmailData.originalContent || viewEmailData.inhalt || ''), isEmbed)}
                </div>
              </div>

              {/* AI Response - nur zeigen wenn KEINE gesendete Antwort vorhanden (sonst redundant) */}
              {viewEmailData.aiAntwort && !viewEmailData.gesendeteAntwort && (
                <div>
                  <p className="text-xs text-text-muted font-semibold mb-1 uppercase">KI-Antwort</p>
                  <div className="p-3 rounded-lg bg-blue-50 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {censorBody(stripHtmlTags(viewEmailData.aiAntwort), isEmbed)}
                  </div>
                </div>
              )}

              {/* Gesendete Antwort */}
              {viewEmailData.gesendeteAntwort && (
                <div>
                  <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Gesendete Antwort</p>
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-sm">
                    <div className="flex flex-wrap gap-3 text-xs text-text-muted mb-2">
                      <span>An: {censorEmail(viewEmailData.gesendeteAntwort.empfaenger, isEmbed)}</span>
                      <span>Betreff: {censorSubject(viewEmailData.gesendeteAntwort.betreff, isEmbed)}</span>
                      <span>{new Date(viewEmailData.gesendeteAntwort.gesendetAm).toLocaleString('de-DE')}</span>
                    </div>
                    <div className="whitespace-pre-wrap max-h-48 overflow-y-auto">
                      {censorBody(stripHtmlTags(viewEmailData.gesendeteAntwort.inhalt), isEmbed)}
                    </div>
                  </div>
                </div>
              )}

              {/* Extracted Data — im Embed-Mode zensiert (kann Namen/Adressen enthalten) */}
              {viewEmailData.aiExtrahierteDaten && !isEmbed && (
                <div>
                  <p className="text-xs text-text-muted font-semibold mb-1 uppercase">Extrahierte Daten</p>
                  <div className="p-3 rounded-lg bg-green-50 text-sm">
                    <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(viewEmailData.aiExtrahierteDaten, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {viewEmailLoading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      )}

      {/* Blacklist Tab */}
      {activeTab === 'blacklist' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <h3 className="text-lg font-semibold">E-Mail-Blacklist</h3>
          <p className="text-sm text-text-muted">
            E-Mails von diesen Adressen werden vom Agent ignoriert.
          </p>

          {!readOnly && (
            <div className="flex items-center gap-2">
              <input
                type="email"
                placeholder="email@beispiel.de"
                value={newBlacklistEmail}
                onChange={(e) => setNewBlacklistEmail(e.target.value)}
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              />
              <button onClick={handleAddBlacklist} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">
                Hinzufügen
              </button>
            </div>
          )}

          <div className="space-y-2">
            {blacklist.map((item, i) => (
              <div key={item.id || i} className="flex items-center justify-between p-3 rounded-lg border border-border">
                <div>
                  <p className="text-sm font-medium">{item.email}</p>
                  <p className="text-xs text-text-muted">{item.grund}</p>
                </div>
                {!readOnly && (
                  <button
                    onClick={async () => {
                      try {
                        await emailAgentApi.removeFromBlacklist(item.email);
                        loadBlacklist();
                        toast.success('Entfernt');
                      } catch (e) {
                        toast.error('Fehler');
                      }
                    }}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Entfernen
                  </button>
                )}
              </div>
            ))}
            {blacklist.length === 0 && (
              <p className="text-sm text-text-muted text-center py-4">Keine Einträge</p>
            )}
          </div>
        </div>
      )}

      {/* Antwort-Sperre Tab */}
      {activeTab === 'antwortsperre' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <div className="flex items-center gap-2">
            <VolumeX className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Auto-Antwort-Sperre</h3>
          </div>
          <p className="text-sm text-text-muted">
            E-Mails von diesen Adressen werden weiterhin verarbeitet und dokumentiert, aber der KI-Agent sendet <strong>keine automatische Antwort</strong>. Ideal für Kontakte, bei denen manueller Austausch stattfindet.
          </p>

          {!readOnly && (
            <div className="flex items-center gap-2">
              <input
                type="email"
                placeholder="email@beispiel.de"
                value={newSperreEmail}
                onChange={(e) => setNewSperreEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddAntwortSperre()}
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              />
              <button onClick={handleAddAntwortSperre} className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">
                Sperre aktivieren
              </button>
            </div>
          )}

          <div className="space-y-2">
            {antwortSperren.map((item, i) => (
              <div key={item.id || i} className="flex items-center justify-between p-3 rounded-lg border border-border bg-primary/5">
                <div className="flex items-center gap-3">
                  <VolumeX className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{item.kontakt}</p>
                    <p className="text-xs text-text-muted">
                      Gesperrt seit {new Date(item.erstelltAm).toLocaleDateString('de-DE')} · {item.grund}
                    </p>
                  </div>
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleRemoveAntwortSperre(item.kontakt)}
                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  >
                    Antwort aktivieren
                  </button>
                )}
              </div>
            ))}
            {antwortSperren.length === 0 && (
              <p className="text-sm text-text-muted text-center py-4">Keine Sperren aktiv – alle E-Mails werden automatisch beantwortet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="p-3 rounded-lg border border-border text-center">
      <p className="text-xl font-bold text-text">{value}</p>
      <p className="text-xs text-text-muted">{label}</p>
    </div>
  );
}
